import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { startsWith, head, isEmpty } from 'ramda';

import { ROUTES } from '../../../../routes/routes';
import messages from './navigation.messages';
import { Wrapper, Overlay, Nav, List, Item, LinkItem, FooterLinks, FooterItem, FooterLink } from './navigation.styles';
import { LOGIN_TAB, REGISTER_TAB } from '../../../../modules/auth/auth.constants';
import { HiddenOnPlatform } from '../../hiddenOnPlatform';
import { BodyScrollLock } from '../../bodyScrollLock';
import { Breakpoints } from '../../../../theme/media';
import { renderWhenNotNil, renderWhenTrueOtherwise } from '../../../utils/rendering';
import { Hamburger } from '../hamburger';

const createNavigationItems = props => [
  {
    routeUrl: ROUTES.filtered,
    message: messages.experiences,
    itemProps: { showIsActive: true },
  },
  {
    routeUrl: props.magazine,
    message: messages.magazine,
    itemProps: { showIsActive: true },
  },
  {
    routeUrl: ROUTES.account.base,
    message: messages.account,
    itemProps: { withDot: true, showIsActive: true },
    hiddenOnPlatformProps: { below: Breakpoints.TABLET_PORTRAIT },
  },
  {
    routeUrl: ROUTES.account.itinerary,
    message: messages.itinerary,
    hiddenOnPlatformProps: { above: Breakpoints.TABLET },
  },
  {
    routeUrl: ROUTES.account.settings,
    message: messages.settings,
    hiddenOnPlatformProps: { above: Breakpoints.TABLET },
  },
];

const createAnonymousNavigationItems = props => [
  {
    routeUrl: ROUTES.filtered,
    message: messages.experiences,
    itemProps: { showIsActive: true },
  },
  {
    routeUrl: props.magazine,
    message: messages.magazine,
    itemProps: { showIsActive: true },
  },
];

export class Navigation extends PureComponent {
  static propTypes = {
    magazine: PropTypes.string.isRequired,
    isSignedIn: PropTypes.bool,
    isOpened: PropTypes.bool,
    currentHomeRoute: PropTypes.string,
    footerLinks: PropTypes.array,
    signOut: PropTypes.func.isRequired,
    showModal: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    onItemClick: PropTypes.func.isRequired,
  };

  getNavigationItems = () => createNavigationItems(this.props).filter(({ routeUrl }) => !isEmpty(routeUrl));
  getAnonymousNavigationItems = () =>
    createAnonymousNavigationItems(this.props).filter(({ routeUrl }) => !isEmpty(routeUrl));

  isActiveItem = route => startsWith(route, this.props.location.pathname);

  handleLogin = () => {
    this.handleItemClick();
    this.props.showModal(LOGIN_TAB);
  };

  handleRegister = () => {
    this.handleItemClick();
    this.props.showModal(REGISTER_TAB);
  };

  handleLogout = () => {
    this.handleItemClick();
    this.props.signOut();
  };

  handleItemClick = () => this.props.onItemClick();

  renderAnonymousNavigation = () => (
    <Fragment>
      {this.getAnonymousNavigationItems().map(this.renderNavigationItem)}
      <Item onClick={this.handleLogin} withDot>
        <FormattedMessage {...messages.login} />
      </Item>
      <Item onClick={this.handleRegister}>
        <FormattedMessage {...messages.signUp} />
      </Item>
    </Fragment>
  );

  renderNavigationItem = ({
    routeUrl,
    message,
    itemProps: { showIsActive, ...itemProps } = {},
    hiddenOnPlatformProps,
  }) => {
    const Wrapper = hiddenOnPlatformProps ? HiddenOnPlatform : Fragment;
    return (
      <Wrapper {...(hiddenOnPlatformProps ? hiddenOnPlatformProps : null)} key={routeUrl}>
        <Item isActive={showIsActive && this.isActiveItem(routeUrl)} {...itemProps}>
          <LinkItem to={routeUrl}>
            <FormattedMessage {...message} />
          </LinkItem>
        </Item>
      </Wrapper>
    );
  };

  renderUserNavigation = () => (
    <Fragment>
      {this.getNavigationItems().map(this.renderNavigationItem)}
      <Item onClick={this.handleLogout}>
        <FormattedMessage {...messages.signOut} />
      </Item>
    </Fragment>
  );

  renderFooterLinks = () => {
    const supportLinks = head(this.props.footerLinks).links;
    return (
      <FooterLinks>
        {supportLinks.map(({ label, url, shouldOpenInNewTab }) => (
          <FooterItem key={label}>
            <FooterLink openInternalInNewTab={shouldOpenInNewTab} to={url}>
              {label}
            </FooterLink>
          </FooterItem>
        ))}
      </FooterLinks>
    );
  };

  renderMobileNavigation = () => (
    <Nav>
      <BodyScrollLock isLocked={this.props.isOpened}>
        <List>
          {renderWhenTrueOtherwise(this.renderUserNavigation, this.renderAnonymousNavigation)(this.props.isSignedIn)}
          {renderWhenNotNil(this.renderFooterLinks)(this.props.footerLinks)}
        </List>
      </BodyScrollLock>
    </Nav>
  );

  renderNavigation = () => (
    <Nav>
      <List>
        {renderWhenTrueOtherwise(this.renderUserNavigation, this.renderAnonymousNavigation)(this.props.isSignedIn)}
      </List>
    </Nav>
  );

  render() {
    return (
      <Wrapper>
        <HiddenOnPlatform above={Breakpoints.TABLET}>
          <Overlay />
          <Hamburger isOpened={this.props.isOpened} onClick={this.handleItemClick} />
          {this.renderMobileNavigation()}
        </HiddenOnPlatform>
        <HiddenOnPlatform below={Breakpoints.TABLET_PORTRAIT}>{this.renderNavigation()}</HiddenOnPlatform>
      </Wrapper>
    );
  }
}
